"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import gsap from "gsap";
import PageLoadingSpinner from "@/components/ui/page-loading-spinner";

export default function PageTransition({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Set opacity to 0 synchronously BEFORE paint on route changes
  // This prevents the flash of content
  useLayoutEffect(() => {
    if (prevPathnameRef.current !== null && prevPathnameRef.current !== pathname && containerRef.current) {
      // Kill any existing tween first
      if (tweenRef.current) {
        tweenRef.current.kill();
      }
      gsap.set(containerRef.current, { opacity: 0 });
      setIsTransitioning(true);
    }
  }, [pathname]);

  useEffect(() => {
    // Skip animation on first mount
    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = pathname;
      return;
    }

    // Only animate if pathname actually changed
    if (prevPathnameRef.current !== pathname && containerRef.current) {
      // Check if navigating within the same section (e.g., between microphones)
      const prevSection = prevPathnameRef.current.split('/')[1];
      const currentSection = pathname.split('/')[1];
      const isSameSection = prevSection === currentSection;

      // Increased delay to 250ms to let ScrollSmoother fully initialize
      const timer = setTimeout(() => {
        if (containerRef.current) {
          // Fade in the new content
          tweenRef.current = gsap.fromTo(
            containerRef.current,
            { opacity: 0 },
            {
              opacity: 1,
              duration: isSameSection ? 0.3 : 0.6,
              ease: "power2.out",
              overwrite: true,
              onComplete: () => {
                setIsTransitioning(false);
              },
            }
          );
        }
      }, 250);

      // Safety fallback: if still invisible after 1.5s, force visible
      const safetyTimer = setTimeout(() => {
        if (containerRef.current) {
          const currentOpacity = getComputedStyle(containerRef.current).opacity;
          if (currentOpacity === '0') {
            gsap.set(containerRef.current, { opacity: 1 });
            setIsTransitioning(false);
          }
        }
      }, 1500);

      prevPathnameRef.current = pathname;
      
      return () => {
        clearTimeout(timer);
        clearTimeout(safetyTimer);
      };
    }
  }, [pathname]);

  return (
    <>
      <main ref={containerRef} className="page-content">
        {children}
      </main>
      <PageLoadingSpinner isLoading={isTransitioning} />
    </>
  );
}
